/**
 * Verification Service
 * Handles all verification workflow API interactions
 */

import apiClient from 'services/apiClient';
import { API_ENDPOINTS } from 'config/api.config';
import {
  VerificationSessionResponse,
  VerificationSubmitRequest,
  VerificationSubmitResponse,
} from 'types/verification.types';

/**
 * Verification Service Class
 */
class VerificationService {
  /**
   * Get verification session status and current question
   * @param sessionId - Verification session ID
   * @returns Promise with session status and question
   */
  async getStatus(sessionId: string): Promise<VerificationSessionResponse> {
    try {
      const response = await apiClient.get<VerificationSessionResponse>(
        API_ENDPOINTS.VERIFICATION.STATUS(sessionId)
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to get verification status';
      throw new Error(errorMessage);
    }
  }

  /**
   * Submit verification response with continue/additional info options
   * @param data - Request data with session_id, continue_with_available_data, and optional additional_information
   * @returns Promise with verification response
   */
  async submitResponse(
    data: VerificationSubmitRequest
  ): Promise<VerificationSubmitResponse> {
    try {
      const response = await apiClient.post<VerificationSubmitResponse>(
        API_ENDPOINTS.VERIFICATION.RESPOND,
        data
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to submit verification response';
      throw new Error(errorMessage);
    }
  }

  /**
   * Continue with available data (no additional information)
   * @param sessionId - Verification session ID
   * @returns Promise with verification response
   */
  async continueWithAvailableData(
    sessionId: string
  ): Promise<VerificationSubmitResponse> {
    return this.submitResponse({
      session_id: sessionId,
      continue_with_available_data: true,
    });
  }

  /**
   * Provide additional information
   * @param sessionId - Verification session ID
   * @param additionalInfo - Additional information in format "field_name: value, field_name: value, ..."
   * @returns Promise with verification response
   */
  async provideAdditionalInformation(
    sessionId: string,
    additionalInfo: string
  ): Promise<VerificationSubmitResponse> {
    return this.submitResponse({
      session_id: sessionId,
      continue_with_available_data: false,
      additional_information: additionalInfo,
    });
  }
}

// Export singleton instance
const verificationService = new VerificationService();
export default verificationService;
